// components/PronunciationTraining.js
import React, { useState, useRef } from 'react';

const PronunciationTraining = ({ onBack }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [score, setScore] = useState(null);
  const mediaRecorderRef = useRef(null);
  const [currentWord] = useState('father');
  
  const playReferenceAudio = () => {
    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      let selectedVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Neural'))
      ) || voices.find(voice => voice.lang.startsWith('en-US')) || voices[0];
      
      const utterance = new SpeechSynthesisUtterance(currentWord);
      utterance.voice = selectedVoice;
      utterance.rate = 0.6; // Slow and clear for reference
      utterance.pitch = 1.0;
      utterance.volume = 0.9;
      utterance.lang = 'en-US';
      
      utterance.onerror = (event) => {
        console.error('Reference audio error:', event.error);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const startRecording = async () => {
    try {
      // Request high-quality audio with specific constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
          sampleSize: 16
        }
      });
      
      // Use high-quality recording options
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      const audioChunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { 
          type: 'audio/webm;codecs=opus' 
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        analyzePronunciation();
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setIsRecording(false);
      };
      
      mediaRecorder.start(100); // Collect data every 100ms for better quality
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const analyzePronunciation = () => {
    // Simulate pronunciation analysis
    setTimeout(() => {
      const mockScore = {
        overall: Math.floor(Math.random() * 20) + 80, // 80-100
        pronunciation: Math.floor(Math.random() * 20) + 85,
        fluency: Math.floor(Math.random() * 15) + 80,
        completeness: Math.floor(Math.random() * 10) + 90
      };
      setScore(mockScore);
    }, 2000);
  };

  const phoneticDemos = [
    { symbol: '[ɪ]', description: 'Short i sound' },
    { symbol: '[e]', description: 'Short e sound' },
    { symbol: '[æ]', description: 'Short a sound' },
    { symbol: '[ɜː]', description: 'Er sound' }
  ];

  return (
    <div className="pronunciation-training">
      <header>
        <button onClick={onBack}>← Back</button>
        <h1>Pronunciation Training</h1>
      </header>
      
      <div className="phonetic-instructor">
        <h2>雪梨老师真人教学</h2>
        <p>港中文翻译硕士带你学口型舌位</p>
        
        <div className="phonetic-demos">
          {phoneticDemos.map((demo, index) => (
            <div key={index} className="phonetic-demo-card">
              <div className="phonetic-symbol">{demo.symbol}</div>
              <div className="mouth-demo">👄</div>
              <p>{demo.description}</p>
              <button className="demo-btn">Watch Demo</button>
            </div>
          ))}
        </div>
      </div>
      
      <div className="pronunciation-practice">
        <div className="word-practice">
          <h2>{currentWord}</h2>
          <div className="phonetic">/ 'fɑːðər /</div>
          <div className="definition">n. 父亲；祖先</div>
          
          <button 
            className="reference-audio-btn"
            onClick={playReferenceAudio}
          >
            🔊 Listen to Reference
          </button>
          
          <div className="recording-section">
            <button 
              className={`record-btn ${isRecording ? 'recording' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
            >
              🎤 {isRecording ? 'Stop Recording' : 'Start Recording'}
            </button>
            
            {recordedAudio && (
              <audio controls src={recordedAudio} className="recorded-audio">
                Your browser does not support the audio element.
              </audio>
            )}
          </div>
          
          {score && (
            <div className="pronunciation-score">
              <h3>Pronunciation Analysis</h3>
              <div className="score-breakdown">
                <div className="score-item">
                  <span>Overall Score:</span>
                  <span className="score-value">{score.overall}/100</span>
                </div>
                <div className="score-item">
                  <span>Pronunciation:</span>
                  <span className="score-value">{score.pronunciation}/100</span>
                </div>
                <div className="score-item">
                  <span>Fluency:</span>
                  <span className="score-value">{score.fluency}/100</span>
                </div>
                <div className="score-item">
                  <span>Completeness:</span>
                  <span className="score-value">{score.completeness}/100</span>
                </div>
              </div>
              
              <div className="feedback">
                <h4>Feedback:</h4>
                <p>Great job! Your pronunciation is very clear. Try to work on the rhythm and intonation.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="ebbinghaus-curve">
        <h3>AI Memory Curve Analysis</h3>
        <div className="curve-chart">
          <div className="chart-placeholder">
            📊 Memory retention curve based on Ebbinghaus research
          </div>
          <p>AI老师根据艾宾浩斯记忆曲线给孩子定制学习计划</p>
        </div>
      </div>
    </div>
  );
};

export default PronunciationTraining;